/**
 * Game Service
 * 
 * This module handles all game-related Firestore operations, including:
 * - Saving game progress
 * - Updating user points
 * - Getting player history
 * 
 * @module firebase/gameService
 */

import { 
  doc, 
  collection, 
  addDoc, 
  updateDoc, 
  increment, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './config';

/**
 * Save game results to Firestore
 * 
 * @param {Object} gameData - Game result data
 * @param {string} gameData.userId - User ID
 * @param {boolean} gameData.isCompleted - Whether the game was completed successfully
 * @param {string} gameData.subject - Subject code (241 or 242)
 * @param {Object} gameData.work - The literary work that was played
 * @param {number} gameData.questionsAsked - Number of questions asked
 * @param {number} gameData.correctAnswers - Number of correctly answered questions
 * @returns {Promise<string>} ID of the created game document
 */
export const saveGameResult = async (gameData) => {
  try {
    const { userId, isCompleted, subject, work, questionsAsked, correctAnswers } = gameData;
    
    // Calculate points for this game: +1 for correct, -1 for incorrect
    const pointsEarned = calculatePoints(questionsAsked, correctAnswers);
    
    // Add game record to games collection
    const gameRef = await addDoc(collection(db, 'games'), {
      userId,
      subject,
      workId: work,
      isCompleted,
      questionsAsked,
      correctAnswers,
      pointsEarned,
      timestamp: serverTimestamp()
    });
    
    // Update user's total points
    await updateUserPoints(userId, pointsEarned);
    
    return gameRef.id;
  } catch (error) {
    console.error('Error saving game result:', error);
    throw error;
  }
};

/**
 * Calculate points earned for a game based on the simplified point system
 * Points system:
 * - 0 points if all answers are wrong
 * - 1 point for each correct answer (max 4 points)
 * 
 * @param {number} questionsAsked - Total number of questions asked
 * @param {number} correctAnswers - Number of correct answers
 * @returns {number} Total points earned (always non-negative)
 */
const calculatePoints = (questionsAsked, correctAnswers) => {
  // No negative points - if all answers wrong, return 0
  if (correctAnswers === 0) {
    return 0;
  }
  
  // Return the number of correct answers (1 point per correct answer)
  return correctAnswers;
};

/**
 * Update user's total points
 * 
 * @param {string} userId - User ID
 * @param {number} pointsEarned - Points earned in the game
 * @returns {Promise<void>}
 */
export const updateUserPoints = async (userId, pointsEarned) => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Just update the total points - no streak or other metrics
    await updateDoc(userRef, {
      totalPoints: increment(pointsEarned),
      lastPlayedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user points:', error);
    throw error;
  }
};

/**
 * Get user's game history
 * 
 * @param {string} userId - User ID
 * @param {number} [limit=10] - Maximum number of records to retrieve
 * @returns {Promise<Array>} Game history records
 */
export const getUserGameHistory = async (userId, limit = 10) => {
  try {
    const gamesQuery = query(
      collection(db, 'games'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(gamesQuery);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error fetching user game history:', error);
    throw error;
  }
};

/**
 * Get subject completion statistics for a user
 * Shows how many works have been completed in each subject
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Subject completion stats
 */
export const getUserSubjectStats = async (userId) => {
  try {
    const gamesQuery = query(
      collection(db, 'games'),
      where('userId', '==', userId),
      where('isCompleted', '==', true)
    );
    
    const querySnapshot = await getDocs(gamesQuery);
    
    // Create a map to count unique works per subject
    const subjectWorksMap = {
      '241': new Set(),
      '242': new Set()
    };
    
    querySnapshot.forEach(doc => {
      const data = doc.data();
      if (data.subject && data.workId) {
        subjectWorksMap[data.subject].add(data.workId);
      }
    });
    
    return {
      '241': subjectWorksMap['241'].size,
      '242': subjectWorksMap['242'].size
    };
  } catch (error) {
    console.error('Error fetching user subject stats:', error);
    throw error;
  }
};

/**
 * Get leaderboard of top players
 * 
 * @param {number} [topCount=10] - Number of top players to retrieve
 * @returns {Promise<Array>} Array of top players
 */
export const getLeaderboard = async (topCount = 10) => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('totalPoints', 'desc'),
      limit(topCount)
    );
    
    const querySnapshot = await getDocs(usersQuery);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      displayName: doc.data().displayName,
      photoURL: doc.data().photoURL,
      totalPoints: doc.data().totalPoints || 0
    }));
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    throw error;
  }
};